import { SimConstants } from "@highmarch/domain";
import {
  Mover, PET_STRIKE_TUNING, Pace, PetStrikeSpec, Spot, StrikeTimes, blowAt, contactGap, contactSide, leadSeconds, spitFlight, steer,
  strikeTimes, travelSeconds,
} from "./petStrike";

/** Where a raider is drawn (world x / z), and how high over the ground the owl strikes it from (0 on the ground; a flyer's height). */
export interface FoeAt { readonly x: number; readonly z: number; readonly lift: number }

/** The sim's next attack as the view reads it (petFoes.ts, from domain petForecast). */
export interface FightCue {
  /** The raider it will strike (-1: none known) and in how many ticks; `sure`: the sim's exact choice now (else a raider walking in). */
  readonly target: number;
  readonly inTicks: number;
  readonly sure: boolean;
  /** A raider coming into reach the pet may go and meet (-1: none). */
  readonly stalk: number;
}

/** What the fight does to the pet's body and the effects (PetView wires these to the body, the VFX and the ImpactGate). */
export interface FightHooks {
  /** Start the strike clip now (`air`: in flight, the owl's swoop). */
  clip(air: boolean): void;
  /** The spit leaves the mouth for raider `target`, flying `flight` seconds. */
  release(target: number, flight: number): void;
  /** The blow lands on raider `target` (the spit's landing): its effect. `key`: the sim's held hit, null while the sim has not struck. */
  hit(target: number, key: number | null): void;
  /** The sim's held hit `key` may show now. */
  land(key: number): void;
}

/** One attack: its raider, timeline, what of it has happened, the sim's held hit once the sim struck. */
interface Blow {
  target: number; times: StrikeTimes; dueTick: number; sure: boolean;
  key: number | null; clipped: boolean; released: boolean; hit: boolean; shown: boolean;
}

/** The frame's pose of the fight, read by PetView (`active` false: the pet is its own, on its spot). */
export interface FightPose {
  active: boolean;
  x: number; z: number;
  /** Height above the ground (the owl's flight), and how fast it changes (units a second: − descending). */
  lift: number; climb: number;
  /** The yaw to face (null: its own facing), and its speed (world units a second). */
  yaw: number | null;
  speed: number;
}

const TICK = 1 / SimConstants.TicksPerSecond;
const T = PET_STRIKE_TUNING;
/** Back on its spot within this (world units) and slower than `still`: the fight lets go of it. */
const HOME_EPS = 0.03;
/** How fast the tracked raider's velocity follows its drawn motion (per second). */
const TRACK_RATE = 8;

/**
 * The pet's fight at the flag (petStrike.ts), as state over the pet's clock: a steered body that goes to meet a raider
 * walking into reach, runs (or flies) to the raider of a blow timed to land on the sim's tick, strikes as it arrives,
 * stays on it while the next blow is due and goes back to its spot. Pure (no GL): test/petStrike.test.ts drives it.
 */
export class PetFight {
  readonly pose: FightPose = { active: false, x: 0, z: 0, lift: 0, climb: 0, yaw: null, speed: 0 };
  private readonly m: Mover = { x: 0, z: 0, vx: 0, vz: 0 };
  private blow: Blow | null = null;
  /** The raider it last struck and until when it stays on it; the raider walking in that it goes to meet. */
  private foe = -1;
  private stayUntil = -Infinity;
  private stalk = -1;
  private readonly goal: Spot = { x: 0, z: 0 };
  private readonly scratch: Spot = { x: 0, z: 0 };
  /** Where it aims (the raider ahead along its way), the tracked raider, its last place and smoothed velocity. */
  private readonly aim: Spot = { x: 0, z: 0 };
  private tracked = -1;
  private readonly last: Spot = { x: 0, z: 0 };
  private readonly tv: Spot = { x: 0, z: 0 };
  private leash = Infinity;
  private readonly gap: number;

  constructor(private readonly spec: PetStrikeSpec, private readonly scale: number, private readonly hooks: FightHooks, readonly pace: Pace) {
    this.gap = contactGap(spec, scale);
  }

  /** The raider it is busy with (-1: none). */
  get target(): number { return this.blow?.target ?? (this.pose.active ? (this.foe >= 0 ? this.foe : this.stalk) : -1); }
  /** Whether a blow is under way. */
  get striking(): boolean { return this.blow !== null && !this.blow.hit; }
  /** When the blow under way shows its hit (pet clock), null when none is due. */
  get hitAt(): number | null { return this.blow && !this.blow.shown ? this.blow.times.hitAt : null; }

  /**
   * The sim struck raider `target` this tick (PetBit); `key` holds its hit (statusFx.ts PET_ROUND). A blow staged for it
   * takes the key (landing it now if the blow already landed); otherwise a blow starts now and lands as soon as it can.
   */
  struck(now: number, target: number, key: number, home: Spot, at: (id: number) => FoeAt | null, cam: Spot): void {
    const b = this.blow;
    if (b && b.target === target && b.key === null) {
      if (b.hit) { if (!b.shown) this.hooks.hit(target, null); this.blow = null; this.hooks.land(key); } else b.key = key;
      return;
    }
    if (b && b.key !== null && !b.shown) this.hooks.land(b.key);         // an earlier blow cut short: its hit shows now
    this.plan(now, target, null, -1, true, home, at, cam);
    if (this.blow) this.blow.key = key; else this.hooks.land(key);
  }

  /**
   * After each tick: the sim's next attack (`cue`). When its lead comes, the blow starts so that it lands on that tick.
   * `near(id)`: whether raider `id` is still within the attack's reach (a raider that walked out of it is let go).
   */
  foresee(now: number, tick: number, cue: FightCue | null, home: Spot, at: (id: number) => FoeAt | null, cam: Spot, near: (id: number) => boolean = () => true): void {
    const b = this.blow;
    if (this.foe >= 0 && !near(this.foe)) this.stayUntil = Math.min(this.stayUntil, now);
    if (b && b.dueTick >= 0 && b.key === null && tick > b.dueTick + T.lateTicks) this.blow = null;   // the sim never struck
    this.stalk = this.spec.style === "spit" || !cue ? -1 : cue.stalk;
    if (this.blow || !cue || cue.target < 0) return;
    const f = at(cue.target);
    if (!f) return;
    const due = blowAt(now, cue.inTicks);
    if (due - now > leadSeconds(this.spec, this.travelTo(f, home, cam), this.flightTo(f, home)) + TICK) return;
    this.plan(now, cue.target, due, tick + cue.inTicks, cue.sure, home, at, cam);
  }

  /** Each frame (`dt` s, pet clock `now`): the body steered, the strike's beats, the pose. `obs`: x, z pairs to steer round. */
  update(now: number, dt: number, home: Spot, at: (id: number) => FoeAt | null, cam: Spot, interval: number, obs: ArrayLike<number> = [], nObs = 0): FightPose {
    const p = this.pose, m = this.m, b = this.blow;
    if (!p.active) { m.x = home.x; m.z = home.z; m.vx = 0; m.vz = 0; }
    const striking = !!b && now >= b.times.runAt;
    let focus = striking ? b!.target : now < this.stayUntil ? this.foe : this.stalk;
    let f = focus >= 0 ? at(focus) : null;
    this.track(focus, f, dt);
    // where it will be by the time the pet gets there (a flyer runs on), and the leash round its spot: only a blow the
    // sim is striking takes it past (that raider is in the sim's reach of the flag, by its spot)
    if (f) {
      const eta = Math.min(T.leadAhead, Math.hypot(f.x - m.x, f.z - m.z) / Math.max(0.1, this.pace.speed));
      this.aim.x = f.x + this.tv.x * eta; this.aim.z = f.z + this.tv.z * eta;
      if (!striking && (Math.hypot(this.aim.x - home.x, this.aim.z - home.z) > this.leash || this.offLimits(this.aim.x, this.aim.z))) {
        if (focus === this.foe) this.stayUntil = Math.min(this.stayUntil, now);
        focus = -1; f = null;
      }
    }
    if (f && this.spec.style !== "spit") contactSide(m.x, m.z, this.aim.x, this.aim.z, cam.x, cam.z, this.gap, this.goal);
    else { this.goal.x = home.x; this.goal.z = home.z; }
    // never over the fortress: a blow on a raider past its walls waits where it is (the sim's hit lands by its deadline)
    if (this.offLimits(this.goal.x, this.goal.z)) { this.goal.x = m.x; this.goal.z = m.z; }
    if (dt > 0) steer(m, this.goal.x, this.goal.z, this.pace.speed, this.pace.accel, dt, obs, p.lift > T.cruiseAlt * 0.5 ? 0 : nObs);
    p.speed = Math.hypot(m.vx, m.vz);
    this.fly(f, dt);
    p.x = m.x; p.z = m.z;
    const atHome = !f && Math.hypot(home.x - m.x, home.z - m.z) < HOME_EPS && p.speed < T.still && p.lift < T.airborne;
    p.active = !atHome || !!b;
    // facing: along the way while it covers ground, else at what it fights
    p.yaw = p.speed > this.pace.walkUnder * 0.5 ? Math.atan2(m.vx, m.vz) : f ? Math.atan2(f.x - m.x, f.z - m.z) : p.speed > T.still ? Math.atan2(m.vx, m.vz) : null;
    if (b) this.beats(now, b, f, interval);
    return p;
  }

  /** The raider it goes for: its drawn velocity, smoothed, so it can aim ahead along a flyer's way. */
  private track(id: number, f: FoeAt | null, dt: number): void {
    if (!f || id !== this.tracked || dt <= 0) {
      this.tracked = f ? id : -1; this.tv.x = 0; this.tv.z = 0;
      if (f) { this.last.x = f.x; this.last.z = f.z; }
      return;
    }
    const k = Math.min(1, dt * TRACK_RATE);
    this.tv.x += ((f.x - this.last.x) / dt - this.tv.x) * k; this.tv.z += ((f.z - this.last.z) / dt - this.tv.z) * k;
    this.last.x = f.x; this.last.z = f.z;
  }

  /** How far from its spot it may go (world units): the attack's reach plus PET_STRIKE_TUNING.leash (petFoes.ts reach). */
  setReach(reach: number): void { this.leash = reach + T.leash; }
  /** Where it may never go (world x / z: the fortress's cells), set by the view. */
  offLimits: (x: number, z: number) => boolean = () => false;

  /** Leaves the fight at once (the level ends, a new body). */
  reset(): void {
    if (this.blow?.key != null && !this.blow.shown) this.hooks.land(this.blow.key);
    this.blow = null; this.stalk = -1; this.foe = -1; this.stayUntil = -Infinity;
    Object.assign(this.pose, { active: false, lift: 0, climb: 0, yaw: null, speed: 0 });
  }

  /** The owl's height: up to its cruise over a run, to the raider's height for the strike, down onto its spot. */
  private fly(f: FoeAt | null, dt: number): void {
    const p = this.pose;
    if (this.spec.style !== "swoop" || dt <= 0) { p.lift = 0; p.climb = 0; return; }
    const away = Math.hypot(this.goal.x - this.m.x, this.goal.z - this.m.z);
    const cruise = T.cruiseAlt * this.scale;
    const want = f ? (away > this.gap * 2 ? Math.max(cruise, f.lift) : Math.max(f.lift, cruise * 0.35)) : away > this.gap ? cruise : 0;
    const step = T.climb * dt, d = want - p.lift;
    const next = p.lift + Math.max(-step, Math.min(step, d));
    p.climb = (next - p.lift) / dt; p.lift = next;
  }

  /** The strike's beats: the clip, the spit's release, the blow (held a moment for a pet not yet on its raider). */
  private beats(now: number, b: Blow, f: FoeAt | null, interval: number): void {
    const t = b.times, spit = this.spec.style === "spit";
    if (!b.clipped && now >= t.clipAt) { b.clipped = true; this.hooks.clip(this.pose.lift > T.airborne); }
    if (spit && !b.released && now >= t.releaseAt) { b.released = true; if (f) this.hooks.release(b.target, t.hitAt - t.releaseAt); }
    if (b.hit || now < t.hitAt) return;
    const off = f && !spit ? Math.hypot(this.goal.x - this.m.x, this.goal.z - this.m.z) : 0;
    if (off > T.contactSlack && now < t.hitAt + T.maxWait) return;
    b.hit = true;
    this.foe = b.target;
    this.stayUntil = now + interval * T.stayIntervals;
    // a blow the sim has confirmed (or surely will) shows its effect now; a guess shows it when the sim strikes
    if (b.key !== null || b.sure) { b.shown = true; this.hooks.hit(b.target, b.key); }
    if (b.key !== null) this.blow = null;
  }

  private plan(now: number, target: number, due: number | null, dueTick: number, sure: boolean, home: Spot, at: (id: number) => FoeAt | null, cam: Spot): void {
    const f = at(target);
    if (!f) { this.blow = null; return; }
    const times = strikeTimes(this.spec, now, due, this.travelTo(f, home, cam), this.flightTo(f, home));
    this.blow = { target, times, dueTick, sure, key: null, clipped: false, released: false, hit: false, shown: false };
  }

  /** Seconds of steered run from where it is to its blow on `f` (0 for the spit). */
  private travelTo(f: FoeAt, home: Spot, cam: Spot): number {
    if (this.spec.style === "spit") return 0;
    const s = this.pose.active ? this.m : home, c = contactSide(s.x, s.z, f.x, f.z, cam.x, cam.z, this.gap, this.scratch);
    return travelSeconds(this.spec, this.pace, Math.hypot(c.x - s.x, c.z - s.z));
  }

  private flightTo(f: FoeAt, home: Spot): number {
    if (this.spec.style !== "spit") return 0;
    return spitFlight(Math.hypot(f.x - home.x, f.z - home.z));
  }
}
